import type { ApiConfig, Language } from '@vista-digital/occ-domain'
import { LoyaltyAuthenticationType, LoyaltyMemberIdentifier, orderDomainStore } from '@vista-digital/occ-domain'
import type { OccDisplayText } from '@vista-digital/occ-ui'
import { defaultOccContextProps, OccContextComponent } from '@vista-digital/occ-ui'
import type { OccContextProps as OccContextComponentProps } from '@vista-digital/occ-ui/src/lib/context/occ/types/props'
import type { NestedPartial } from '@vista-digital/utilities'
import { runInAction } from 'mobx'
import type { ComponentType, FC, LazyExoticComponent, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useLogger } from '../../../hooks'
import { occService, Services } from '../../../services'
import { cancelOrder } from '../../../utils/cancelOrder'
import type { OccConfigurationOverridePropsApi } from '../occConfigurationOverride'
import { type OccContextProps } from './occConfiguration.models'
import {
    getAssets,
    getFilmConfig,
    getFilterConfig,
    getFormValidationConfig,
    getMemberConfig,
    getNotificationConfig,
    getOccAccessibilityText,
    getOccContextDisplayText,
    getOccLocale,
    getOrderConfig,
} from './props'
import { getTicketRedemptionCodeTypes } from './ticketRedemptionCodes/getTicketRedemptionCodeTypes'

type OccContextCompProps = {
    children?: ReactNode | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- for lazy loading
    customShowtimeButton?: LazyExoticComponent<ComponentType<any>>
    changeOccContextDisplayText?: (data: OccDisplayText) => OccDisplayText
    languages?: Language[]
} & NestedPartial<Pick<OccContextComponentProps, 'config' | 'domain'>> // picking config for now could be expanded

export const OccContext: FC<OccContextCompProps> = props => {
    const logger = useLogger('OccContext')
    const data = Services.CmsDataService.get()

    logger.debug('OCC Context initialised with data', data)

    const [occConfigurationOverride, setOccConfigurationOverride] = useState<
        Partial<Omit<OccConfigurationOverridePropsApi, 'vistaCinema'>>
    >({})

    const [occOverrideData, setOccOverrideData] = useState<OccConfigurationOverridePropsApi | undefined>(undefined)

    occService.overrideSiteIdObs.observe_(({ oldValue, newValue }) => {
        if (newValue !== oldValue) {
            runInAction(() => {
                const occOverride = Services.CmsDataService.getOccOverride(newValue as string)
                if (occOverride !== occOverrideData) {
                    setOccOverrideData(occOverride)
                    const excludedVistaCinema = { ...occOverride }
                    delete excludedVistaCinema.vistaCinema
                    setOccConfigurationOverride(excludedVistaCinema)
                }
            })
        }
    })

    const api: NestedPartial<ApiConfig> = useMemo(() => {
        return {
            url: data.api.apiUrl,
            authTokenProvider: () => data.api.authToken, // TODO: make it refresh
            regionCode: data.api.regionCode,
        }
    }, [data.api.apiUrl, data.api.authToken, data.api.regionCode])

    const cdn = useMemo(() => {
        return {
            vista: { url: data.api.vistaCdn },
            moviexchange: { url: data.api.movieExchangeCdn },
        }
    }, [data.api.movieExchangeCdn, data.api.vistaCdn])

    const languages = useMemo((): Language[] => {
        if (props.languages) return props.languages

        return [
            {
                languageTag: 'en',
                label: 'English',
                iconName: 'united-kingdom-flag',
                isDefault: true,
            },
        ]
    }, [props.languages])

    const occConfiguration: OccContextProps = useMemo(() => {
        return {
            ...data.occConfiguration,
            ...occConfigurationOverride,
        }
    }, [data.occConfiguration, occConfigurationOverride])

    const domainProps = useMemo(() => {
        return {
            api: api,
            languages: languages,
            browsing: props.domain?.browsing ?? { maximumSelectedSites: 5 },
            cache: {
                durations: {
                    // Changed as part of COMS-15656, to be reverted with OCC 15 upgrade
                    showtimes: 20,
                    films: 20,
                    items: 20,
                    sites: 20,
                    tickets: 20,
                    member: 20,
                    loyaltyMasterData: 20,
                    seatingMasterData: 20,
                    subscriptions: 20,
                    events: 20,
                    orders: 20,
                    paymentMethods: 20,
                    journeys: 20,
                },
            },
            cdn,
            ticketRedemptionCodeTypes: getTicketRedemptionCodeTypes(),
            cxm: data?.cxmConfiguration ?? { enabled: false },
            loyalty: {
                authentication: {
                    type: LoyaltyAuthenticationType.Cookie,
                    memberIdentifier: LoyaltyMemberIdentifier.Email,
                },
                onSignedOut: () => {
                    orderDomainStore.hydrate()
                    void cancelOrder()
                    localStorage.clear()
                    sessionStorage.clear()
                    window.location.reload()
                },
            },
            order: getOrderConfig(occConfiguration),
        }
    }, [api, cdn, data?.cxmConfiguration, languages, occConfiguration])

    const configProps = useMemo(() => {
        return {
            enableSkeletonLoadingIndicators: true,
            animation: {
                enableAnimations: false,
            },
            locale: getOccLocale(occConfiguration),
            security: { captcha: data.captcha.siteKey ? data.captcha : undefined },
            form: {
                enableFloatingInputLabels: false,
                fieldValidation: getFormValidationConfig(occConfiguration, props.config?.form?.fieldValidation),
            },
            film: {
                ...getFilmConfig(occConfiguration),

                // Custom film actions require loading data async, which would trigger a rerender and a flicker of the whole page if loaded in the occ context.
                // Instead, they are loaded in PageWrapper.tsx when the first page load is detected, and then set directly using useOccConfig().film.customActions
                customActions: {},
            },
            filter: getFilterConfig(occConfiguration),
            member: getMemberConfig(occConfiguration),
            notification: getNotificationConfig(occConfiguration),
            // showtime:
        }
    }, [data.captcha, occConfiguration])

    const displayTextProps = useMemo(() => {
        if (props?.changeOccContextDisplayText) {
            return props.changeOccContextDisplayText(getOccContextDisplayText(occConfiguration))
        }
        return getOccContextDisplayText(occConfiguration)
    }, [occConfiguration, props.changeOccContextDisplayText])

    const accessibilityTextProps = useMemo(() => getOccAccessibilityText(occConfiguration), [occConfiguration])
    const assetsProps = useMemo(() => getAssets(occConfiguration), [occConfiguration])

    if (!props?.children) return null

    return (
        <OccContextComponent
            domain={domainProps}
            config={configProps}
            displayText={displayTextProps}
            children={props?.children}
            accessibilityText={accessibilityTextProps}
            assets={assetsProps}
            subcomponents={{
                ShowtimeButton: props?.customShowtimeButton || defaultOccContextProps.subcomponents.ShowtimeButton,
            }}
            // placeholderAssets={}
        />
    )
}
